import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';

import { nsLayoutStyles } from '../../../../ns-styles/index.js';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';

import { callLambda } from '../../../../helpers/lambda.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';
import definition from '@job-schemas/support/behaviour-score-failing.json';
import { jobSchema } from '../../job-schema.js';
import { failingView, scoreNowResult, type ScoreAnswer } from './behaviour-score-failing-logic.js';

/**
 * The nodeStream support card the trust-score/review-queue cron raises when a published company's nightly behavioural score
 * (trust-score/behaviour) has failed without a break for 12 hours (tried again every hour), so its Application interaction and Supply chain scores have
 * stopped moving. Support claims it, finds out why (the reason is the last failure's), can score the company again at once
 * (trust-score/behaviour-score), and marks it resolved. One card per run of failures: the next score that works ends the run.
 */
@customElement(NSSupportBehaviourScoreFailing.is)
export default class NSSupportBehaviourScoreFailing extends NSJobComponent {
  static get is() {
    return 'ns-support-behaviour-score-failing';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { resolved: { type: 'hidden' } } });
  }

  static styles = [nsLayoutStyles, ...super.styles];

  @state()
  private __busy = false;

  @state()
  private __result: { ok: boolean; message: string } | null = null;

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private async __scoreNow() {
    const companyId = failingView(this._job?.inputValues).companyId;
    if (!companyId || this.__busy) return;
    this.__busy = true;
    this.clearError();
    this.__result = null;
    this.loading('Scoring the behaviour score');
    try {
      const response = await callLambda(
        'trust-score/behaviour-score',
        'POST',
        this._auth?.token,
        JSON.stringify({ companyId }),
      );
      this.__result = scoreNowResult(response.res as unknown as ScoreAnswer);
    } catch (err) {
      this.__result = {
        ok: false,
        message: lambdaRefusal(err, 'It failed again. Check the lambda logs for the reason.'),
      };
    } finally {
      this.loaded();
      this.__busy = false;
    }
  }

  private async __resolved() {
    if (this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await this.outcome('resolved');
    } catch (err) {
      this.showError(err, 'Unable to mark this resolved. Please try again.');
    } finally {
      this.__busy = false;
    }
  }

  render() {
    const view = failingView(this._job?.inputValues);
    const result = this.__result;
    return html`
      <ns-job-layout>
        <p slot="intro">${view.companyName}'s behaviour score keeps failing to work out.</p>
        <div class="job-panel">
          ${result
            ? html`
                <wa-callout variant=${result.ok ? 'success' : 'danger'} appearance="filled-outlined">
                  ${result.message}
                </wa-callout>
              `
            : nothing}
          <ns-detail-list
            .rows=${[
              { label: 'Company', value: view.companyName },
              { label: 'Company ID', value: view.companyId },
              { label: 'Failing since', value: view.since ?? 'Unknown' },
              { label: 'Attempts failed in a row', value: view.failures === null ? 'Unknown' : String(view.failures) },
              { label: 'Last failure', value: view.reason ?? 'Unknown' },
            ]}
          ></ns-detail-list>
          <p>
            Each night its Application interaction and Supply chain scores are worked out again. For this company that
            has failed every attempt for at least 12 hours, so they show the last score that worked. Find out why from
            the last failure, then score it again. Mark this resolved once it works or you know why it doesn't.
          </p>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>What happens next</h3>
          <p>
            The nightly run keeps trying every hour. The next score that works ends this run of failures, and a new run
            of failures raises a new card.
          </p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    return html`
      <wa-button
        variant="neutral"
        appearance="outlined"
        type="button"
        ?disabled=${this.__busy}
        @click=${() => this.__scoreNow()}
      >
        Score now
      </wa-button>
      <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__resolved()}>
        Resolved
      </wa-button>
    `;
  }
}
